/**
 * AI Insights & Risk Analysis Card Component
 * Professional risk analysis display with modal expansion
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import StaleAppsComponent from './StaleAppsComponent';
import profile360Service from '../services/profile360Service';

// Styled components
const InsightsCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
  },
}));

const RiskScoreContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: `conic-gradient(from 0deg, ${theme.palette.error.main} 0deg, ${theme.palette.error.main} 87deg, ${theme.palette.grey[200]} 87deg, ${theme.palette.grey[200]} 360deg)`,
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 60,
    height: 60,
    borderRadius: '50%',
    backgroundColor: theme.palette.background.paper,
  }
}));

const RiskScore = styled(Typography)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  fontWeight: 'bold',
  fontSize: '1.5rem',
  color: theme.palette.text.primary
}));

// Impact bar component
const ImpactBar = ({ level }) => {
  const getColor = () => {
    switch (level) {
      case 'high': return '#f44336';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: 4,
        backgroundColor: '#f5f5f5',
        borderRadius: 2,
        overflow: 'hidden',
        mt: 1
      }}
    >
      <Box
        sx={{
          width: level === 'high' ? '100%' : level === 'medium' ? '66%' : '33%',
          height: '100%',
          backgroundColor: getColor(),
          borderRadius: 2,
          transition: 'width 0.3s ease'
        }}
      />
    </Box>
  );
};

// Mock data service
const fetchRiskData = async () => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  return {
    riskScore: 87,
    totalInsights: 9,
    highRisk: 4,
    mediumRisk: 4,
    lowRisk: 1,
    risks: [
      {
        label: 'Dormant Account',
        description: 'Account inactivity > 90 days',
        level: 'medium',
        impact: 'medium'
      },
      {
        label: 'Privilege Creep',
        description: 'More roles than needed',
        level: 'high',
        impact: 'high'
      },
      {
        label: 'Suspicious Activity',
        description: 'Access from 2 regions',
        level: 'high',
        impact: 'high'
      },
      {
        label: 'Behavioral Anomalies',
        description: 'Login at 3AM',
        level: 'high',
        impact: 'high'
      },
      {
        label: 'Excessive Access',
        description: 'Access to 14 systems',
        level: 'medium',
        impact: 'medium'
      },
      {
        label: 'Stale Access',
        description: 'No stale access',
        level: 'low',
        impact: 'low'
      },
      {
        label: 'Access Change Velocity',
        description: '4 changes in 24h',
        level: 'medium',
        impact: 'medium'
      },
      {
        label: 'Peer Access Comparison',
        description: 'Above peer average',
        level: 'medium',
        impact: 'medium'
      },
      {
        label: 'Compliance Risk',
        description: 'Policy breach potential',
        level: 'high',
        impact: 'high'
      }
    ]
  };
};

export default function AiInsightsRiskCard({ userEmail }) {
  const [insights, setInsights] = useState({
    riskScore: 87,
    totalInsights: 9,
    highRisk: 4,
    mediumRisk: 4,
    lowRisk: 1
  });
  const [risks, setRisks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showStaleAppsDetail, setShowStaleAppsDetail] = useState(false);
  const [selectedRiskFilter, setSelectedRiskFilter] = useState(null); // 'high', 'medium', 'low', or null for all
  const [staleAccessData, setStaleAccessData] = useState(null); // Track real stale access data

  // Handle stale access data updates
  const handleStaleAccessData = (data) => {
    setStaleAccessData(data);
  };

  // Update risks with real stale access data
  const getUpdatedRisks = useCallback(() => {
    return risks.map(risk => {
      if (risk.label === 'Stale Access' && staleAccessData) {
        return {
          ...risk,
          level: staleAccessData.riskLevel,
          description: staleAccessData.count > 0
            ? `${staleAccessData.count} stale application${staleAccessData.count > 1 ? 's' : ''} detected`
            : 'No stale access'
        };
      }
      return risk;
    });
  }, [risks, staleAccessData]);

  // Calculate dynamic insights based on real data
  const calculateInsights = useCallback(() => {
    const updatedRisks = getUpdatedRisks();
    const stats = {
      riskScore: 87,
      totalInsights: updatedRisks.length,
      highRisk: 0,
      mediumRisk: 0,
      lowRisk: 0
    };

    updatedRisks.forEach(risk => {
      if (risk.level === 'high') stats.highRisk++;
      else if (risk.level === 'medium') stats.mediumRisk++;
      else if (risk.level === 'low') stats.lowRisk++;
    });

    return stats;
  }, [getUpdatedRisks]);



  // Fetch mock data on mount
  useEffect(() => {
    (async () => {
      const data = await fetchRiskData();
      setRisks(data.risks);
    })();
  }, []);

  // Update insights when risks or stale access data changes
  useEffect(() => {
    if (risks.length > 0) {
      const dynamicInsights = calculateInsights();
      setInsights(dynamicInsights);
    }
  }, [risks, calculateInsights]);

  // Reset stale access data when userEmail changes
  useEffect(() => {
    if (userEmail) {
      console.log('🔄 User email changed, resetting stale access data for:', userEmail);

      // Clear the entire cache to ensure fresh data for new user
      try {
        profile360Service.cacheService.clear();
        console.log('🗑️ Cleared cache for user:', userEmail);
      } catch (error) {
        console.warn('⚠️ Could not clear cache:', error);
      }

      setStaleAccessData(null); // Reset to trigger fresh data load
    }
  }, [userEmail]);

  // Function to handle risk filter clicks
  const handleRiskFilterClick = (riskLevel) => {
    setSelectedRiskFilter(selectedRiskFilter === riskLevel ? null : riskLevel);
  };



  // Function to get filtered risks based on selected filter
  const getFilteredRisks = () => {
    const updatedRisks = getUpdatedRisks();
    if (!selectedRiskFilter) return updatedRisks;
    return updatedRisks.filter(risk => risk.level === selectedRiskFilter);
  };

  // Show exactly 2 items in the main view for more compact design
  const limitedRisks = risks.slice(0, 2);

  return (
    <Box>
      {/* Hidden StaleAppsComponent to load real data in background */}
      <Box sx={{ display: 'none' }}>
        <StaleAppsComponent
          key={userEmail} // Force re-mount when userEmail changes
          userEmail={userEmail}
          onDataLoad={handleStaleAccessData}
        />
      </Box>

      {/* Main Card */}
      <Card
        sx={{
          p: 2,
          cursor: 'pointer',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
          }
        }}
        onClick={() => setShowModal(true)}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            AI Insights & Risk
          </Typography>
          <RiskScoreContainer>
            <RiskScore>{insights.riskScore}</RiskScore>
          </RiskScoreContainer>
        </Box>

        {/* Top Stats Row */}
        <Grid container spacing={1} mb={2}>
          <Grid item xs={4}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h6" color="error.main" fontWeight="bold">
                {insights.highRisk}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                High Risk
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h6" color="warning.main" fontWeight="bold">
                {insights.mediumRisk}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Medium Risk
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h6" color="success.main" fontWeight="bold">
                {insights.lowRisk}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Low Risk
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Limited Insights Cards */}
        <Grid container spacing={1}>
          {limitedRisks.map((risk, index) => (
            <Grid item xs={12} key={index}>
              <InsightsCard sx={{ p: 1.5, minHeight: 80 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {risk.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {risk.description}
                </Typography>
                <ImpactBar level={risk.level} />
              </InsightsCard>
            </Grid>
          ))}
        </Grid>

        {/* View All Button */}
        {risks.length > 2 && (
          <Box mt={2} textAlign="center">
            <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
              View All {risks.length} Insights →
            </Typography>
          </Box>
        )}
      </Card>

      {/* Modal */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '70vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" fontWeight="bold">
                AI Insights & Risk Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Comprehensive security and access insights
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <RiskScoreContainer>
                <RiskScore>{insights.riskScore}</RiskScore>
              </RiskScoreContainer>
              <IconButton onClick={() => setShowModal(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {showStaleAppsDetail ? (
            // Detailed Stale Apps View
            <Box>
              <Box display="flex" alignItems="center" mb={2}>
                <Button
                  onClick={() => setShowStaleAppsDetail(false)}
                  sx={{ mr: 2 }}
                >
                  ← Back
                </Button>
                <Typography variant="h6">Stale Access Details</Typography>
              </Box>
              <StaleAppsComponent
                key={userEmail} // Force re-mount when userEmail changes
                userEmail={userEmail}
                onDataLoad={handleStaleAccessData}
              />
            </Box>
          ) : (
            // Main Insights Overview
            <Box>
              {/* Top Stats Row - Clickable for filtering */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={3}>
                  <Card
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      cursor: 'pointer',
                      border: selectedRiskFilter === 'high' ? '2px solid' : '1px solid',
                      borderColor: selectedRiskFilter === 'high' ? 'primary.main' : 'divider',
                      backgroundColor: selectedRiskFilter === 'high' ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        borderColor: 'primary.light',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
                      }
                    }}
                    onClick={() => handleRiskFilterClick('high')}
                  >
                    <Typography variant="h3" color="error.main" fontWeight="bold">
                      {insights.highRisk}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      High Risk
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      cursor: 'pointer',
                      border: selectedRiskFilter === 'medium' ? '2px solid' : '1px solid',
                      borderColor: selectedRiskFilter === 'medium' ? 'primary.main' : 'divider',
                      backgroundColor: selectedRiskFilter === 'medium' ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        borderColor: 'primary.light',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
                      }
                    }}
                    onClick={() => handleRiskFilterClick('medium')}
                  >
                    <Typography variant="h3" color="warning.main" fontWeight="bold">
                      {insights.mediumRisk}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Medium Risk
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      cursor: 'pointer',
                      border: selectedRiskFilter === 'low' ? '2px solid' : '1px solid',
                      borderColor: selectedRiskFilter === 'low' ? 'primary.main' : 'divider',
                      backgroundColor: selectedRiskFilter === 'low' ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        borderColor: 'primary.light',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
                      }
                    }}
                    onClick={() => handleRiskFilterClick('low')}
                  >
                    <Typography variant="h3" color="success.main" fontWeight="bold">
                      {insights.lowRisk}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Low Risk
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      cursor: 'pointer',
                      border: selectedRiskFilter === null ? '2px solid' : '1px solid',
                      borderColor: selectedRiskFilter === null ? 'primary.main' : 'divider',
                      backgroundColor: selectedRiskFilter === null ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        borderColor: 'primary.light',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
                      }
                    }}
                    onClick={() => setSelectedRiskFilter(null)}
                  >
                    <Typography variant="h3" color="primary.main" fontWeight="bold">
                      {insights.totalInsights}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Insights
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* Filter indicator */}
              {selectedRiskFilter && (
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {selectedRiskFilter} risk insights only. Click "Total Insights" to show all.
                  </Typography>
                </Box>
              )}

              {/* Insights Cards Grid */}
              <Grid container spacing={2}>
                {getFilteredRisks().length > 0 ? (
                  getFilteredRisks().map((risk, index) => (
                    <Grid item xs={4} key={index}>
                      <InsightsCard
                        sx={{
                          p: 2,
                          minHeight: 140,
                          cursor: risk.label === 'Stale Access' ? 'pointer' : 'default'
                        }}
                        onClick={risk.label === 'Stale Access' ? () => setShowStaleAppsDetail(true) : undefined}
                      >
                        <Typography variant="h6" gutterBottom>{risk.label}</Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {risk.description}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          Impact Level
                        </Typography>
                        <ImpactBar level={risk.level} />
                      </InsightsCard>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Box textAlign="center" py={4}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No {selectedRiskFilter} risk insights found
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => setSelectedRiskFilter(null)}
                        sx={{ mt: 2 }}
                      >
                        Show All Insights
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Last updated: {new Date().toLocaleDateString()}
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                setShowModal(false);
                setSelectedRiskFilter(null); // Reset filter when closing modal
                setShowStaleAppsDetail(false); // Reset stale apps detail view
              }}
              sx={{ px: 4 }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
